import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, ArrowRight, Phone } from 'lucide-react';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import './Projects.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Service type mapping
const SERVICE_LABELS = {
  'Projekte': 'Gartenprojekt',
  'Gartenpflege': 'Gartenpflege',
  'Baumfällung': 'Baumfällung',
  'Erdarbeiten': 'Erdarbeiten',
  'Entsorgung': 'Entsorgung',
  'Hochdruckreinigung': 'Hochdruckreinigung'
};

// Get image URL from post - handles both Cloudinary URLs and local /api/uploads paths
const getProjectImage = (post) => {
  if (post.images && post.images.length > 0) {
    const imageUrl = post.images[0].url;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${BACKEND_URL}${imageUrl}`;
  }
  return null;
};

// Get before/after images
const getBeforeAfterImages = (post) => {
  if (!post?.is_before_after || !post?.images) return null;
  
  const beforeImages = post.images.filter(img => img.image_type === 'before');
  const afterImages = post.images.filter(img => img.image_type === 'after');
  
  if (beforeImages.length > 0 && afterImages.length > 0) {
    const beforeUrl = beforeImages[0].url;
    const afterUrl = afterImages[0].url;
    
    const finalBeforeUrl = beforeUrl.startsWith('http') ? beforeUrl : `${BACKEND_URL}${beforeUrl}`;
    const finalAfterUrl = afterUrl.startsWith('http') ? afterUrl : `${BACKEND_URL}${afterUrl}`;
    
    return { before: finalBeforeUrl, after: finalAfterUrl };
  }
  return null;
};

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Projekte | Nessenius Garten & Landschaftsbau';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Aktuelle Projekte von Nessenius Garten & Landschaftsbau. Gartenpflege, Erdarbeiten, Baumfällung und mehr in Göttingen, Nörten-Hardenberg und Umgebung.');
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/posts`);
        if (response.ok) {
          const data = await response.json();
          if (data.posts && data.posts.length > 0) {
            const transformedPosts = data.posts.map((post) => {
              // Get main image - same logic as Home.jsx
              let mainImageUrl = null;
              if (post.images && post.images.length > 0) {
                const imgUrl = post.images[0].url;
                mainImageUrl = imgUrl.startsWith('http') ? imgUrl : `${BACKEND_URL}${imgUrl}`;
              }
              
              // Get before/after images
              let beforeAfterImages = null;
              if (post.is_before_after && post.images) {
                const beforeImgs = post.images.filter(img => img.image_type === 'before');
                const afterImgs = post.images.filter(img => img.image_type === 'after');
                if (beforeImgs.length > 0 && afterImgs.length > 0) {
                  const bUrl = beforeImgs[0].url;
                  const aUrl = afterImgs[0].url;
                  beforeAfterImages = {
                    before: bUrl.startsWith('http') ? bUrl : `${BACKEND_URL}${bUrl}`,
                    after: aUrl.startsWith('http') ? aUrl : `${BACKEND_URL}${aUrl}`
                  };
                }
              }
              
              return {
                ...post,
                mainImage: mainImageUrl,
                beforeAfter: beforeAfterImages
              };
            });
            setPosts(transformedPosts);
          }
        }
      } catch (err) {
        console.error('Error fetching posts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  // Get unique categories from posts
  const categories = ['all', ...new Set(posts.map(p => p.category).filter(Boolean))];
  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(p => p.category === selectedCategory);

  return (
    <div className="projects-page">
      <section className="projects-hero">
        <div className="container">
          <h1>Unsere Projekte</h1>
          <p>Referenzen und abgeschlossene Arbeiten aus Ihrer Region</p>
        </div>
      </section>

      <section className="projects-content-section">
        <div className="container">
          {/* Category Filter */}
          <div className="category-filter">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                data-testid={`filter-${category}`}
              >
                {category === 'all' ? 'Alle Projekte' : SERVICE_LABELS[category] || category}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div className="spinner" style={{
                border: '4px solid #f3f3f3',
                borderTop: '4px solid var(--brand-dark)',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
            </div>
          )}

          {/* Projects Grid */}
          {!loading && (
            <div className="projects-full-grid">
              {filteredPosts.map((post) => {
                // Use pre-computed values from transform
                const beforeAfter = post.beforeAfter;
                const mainImage = post.mainImage;
                
                return (
                  <div key={post.id || post._id} className="project-full-card network-card hover-lift" data-testid={`project-card-${post.slug}`}>
                    <div className="project-full-image-wrapper">
                      {beforeAfter ? (
                        <div className="project-slider-wrapper">
                          <BeforeAfterSlider
                            beforeImage={beforeAfter.before}
                            afterImage={beforeAfter.after}
                          />
                        </div>
                      ) : mainImage ? (
                        <img src={mainImage} alt={post.title} className="project-full-image" loading="lazy" />
                      ) : (
                        <div className="project-placeholder">
                          <span>🌿</span>
                        </div>
                      )}
                      <div className={`project-badges ${beforeAfter ? 'badges-below-slider' : ''}`}>
                        {post.location && (
                          <span className="project-location-badge">
                            <MapPin size={12} />
                            {post.location}
                          </span>
                        )}
                        <span className="project-full-category">{SERVICE_LABELS[post.category] || post.category}</span>
                      </div>
                    </div>
                    <div className="project-full-content">
                      <h3 className="heading-3">{post.title}</h3>
                      <p className="body-medium" style={{ marginTop: '8px', color: 'var(--text-secondary)' }}>
                        {post.excerpt || post.content?.substring(0, 100)}
                      </p>
                      <div className="project-date caption" style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={14} />
                        Projekt abgeschlossen am {new Date(post.created_at).toLocaleDateString('de-DE', { 
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric' 
                        })}
                      </div>
                      
                      {/* CTA Buttons */}
                      <div className="project-card-ctas" style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <Link to="/kontakt" className="project-cta-btn primary">
                          Angebot anfragen
                          <ArrowRight size={16} />
                        </Link>
                        <a href="tel:+4917655669090" className="project-cta-btn secondary">
                          <Phone size={16} />
                          Anrufen
                        </a>
                      </div>
                      
                      {/* Detail Link */}
                      <Link to={`/projekte/${post.slug}`} className="project-detail-link" data-testid={`view-project-${post.slug}`}>
                        Mehr Details ansehen →
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && filteredPosts.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <p className="body-large">Keine Projekte in dieser Kategorie gefunden.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Projects;
